import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSEO } from '../hooks/useSEO';

const TermsOfService = () => {
  useSEO({
    title: "Terms of Service | AM CARDMATE",
    description: "Read AM CARDMATE's terms of service and conditions for using our digital business card platform and services.",
    image: "/assets/terms.jpg",
    keywords: "terms of service, terms and conditions, legal, user agreement, service terms"
  });

  useEffect(() => {
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Terms of Service",
        "description": "Terms and conditions for using AM CARDMATE services",
        "url": "https://amcardmate.com/terms",
        "dateModified": "2025-01-15",
        "publisher": {
          "@type": "Organization",
          "name": "AM CARDMATE"
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "AM CARDMATE Digital Business Cards",
        "serviceType": "Digital Business Card Platform",
        "provider": {
          "@type": "Organization",
          "name": "AM CARDMATE Ltd",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "GB"
          }
        },
        "termsOfService": "https://amcardmate.com/terms"
      }
    ];
    
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
  }, []);

  const [activeSection, setActiveSection] = useState(0);
  
  const sections = [
    {
      title: 'Acceptance of Terms',
      icon: 'handshake',
      content: `By downloading, installing, or using AM CARDMATE, you agree to be bound by these Terms of Service. If you disagree with any part of these terms, you do not have permission to access or use our service.`
    },
    {
      title: 'Use License',
      icon: 'certificate',
      content: `We grant you a revocable, non-exclusive, non-transferable, limited license to download, install, and use AM CARDMATE for your personal and business networking purposes in accordance with these Terms.`
    },
    {
      title: 'Account Registration',
      icon: 'user-plus',
      subsections: [
        {
          subtitle: 'Account Creation',
          points: [
            'You must provide accurate and complete information',
            'You are responsible for maintaining account security',
            'One person or entity per account unless authorized',
            'You must be at least 16 years old to use AM CARDMATE'
          ]
        },
        {
          subtitle: 'Account Responsibilities',
          points: [
            'Keep your password confidential',
            'Notify us immediately of unauthorized access',
            'You are responsible for all activities under your account',
            'We reserve the right to suspend or terminate accounts'
          ]
        }
      ]
    },
    {
      title: 'Subscriptions & Payments',
      icon: 'credit-card',
      subsections: [
        {
          subtitle: 'Subscription Terms',
          points: [
            'Subscriptions auto-renew unless cancelled',
            'Prices may change with 30 days notice',
            'Payments processed through App Store or Google Play',
            'Subscription management through your device settings'
          ]
        },
        {
          subtitle: 'Refund Policy',
          points: [
            'Digital subscriptions follow App Store/Google Play policies',
            'Generally non-refundable unless required by law',
            'Cancellation stops future charges only',
            'Pro-rated refunds not available'
          ]
        },
        {
          subtitle: 'Physical Products',
          points: [
            'NFC cards and accessories are FINAL SALE',
            'NO RETURNS OR REFUNDS on physical products',
            'Defective items may be replaced within 30 days',
            'Shipping costs are non-refundable'
          ]
        }
      ]
    },
    {
      title: 'Acceptable Use',
      icon: 'check-circle',
      subsections: [
        {
          subtitle: 'You May',
          points: [
            'Create professional digital business cards',
            'Share your cards via NFC, QR, or links',
            'Store and organize contacts in collections',
            'Use analytics to track engagement (Premium)'
          ]
        },
        {
          subtitle: 'You May Not',
          points: [
            'Use the service for illegal purposes',
            'Impersonate others or provide false information',
            'Attempt to hack, reverse engineer, or disrupt the service',
            'Share inappropriate, offensive, or harmful content',
            'Resell or commercially exploit the service',
            'Use automated systems or bots',
            'Violate intellectual property rights'
          ]
        }
      ]
    },
    {
      title: 'Intellectual Property',
      icon: 'copyright',
      content: `AM CARDMATE and its original content, features, and functionality are owned by AM CARDMATE Ltd. and are protected by international copyright, trademark, and other intellectual property laws. You retain ownership of content you create but grant us license to display and share it as part of the service.`
    },
    {
      title: 'Privacy & Data',
      icon: 'lock',
      content: `Your use of AM CARDMATE is also governed by our Privacy Policy. By using the service, you consent to our collection and use of personal information as detailed in the Privacy Policy.`
    },
    {
      title: 'Disclaimers',
      icon: 'exclamation-triangle',
      subsections: [
        {
          subtitle: 'Service Availability',
          points: [
            'Service provided "as is" without warranties',
            'We do not guarantee uninterrupted service',
            'Features may change or be discontinued',
            'Not responsible for third-party services'
          ]
        },
        {
          subtitle: 'Limitation of Liability',
          points: [
            'Not liable for indirect or consequential damages',
            'Total liability limited to amount paid in last 12 months',
            'Not responsible for lost data or profits',
            'Some jurisdictions do not allow liability limitations'
          ]
        }
      ]
    },
    {
      title: 'Termination',
      icon: 'times-circle',
      subsections: [
        {
          subtitle: 'By You',
          points: [
            'Cancel subscription anytime through app stores',
            'Delete your account through the app',
            'Termination does not entitle refunds'
          ]
        },
        {
          subtitle: 'By Us',
          points: [
            'For violation of these Terms',
            'For fraudulent or illegal activities',
            'For non-payment of fees',
            'At our discretion with notice'
          ]
        }
      ]
    },
    {
      title: 'Changes to Terms',
      icon: 'edit',
      content: `We reserve the right to modify these Terms at any time. We will notify users of significant changes via the app or email. Continued use after changes constitutes acceptance of new Terms.`
    }
  ];

  const importantNotes = [
    {
      icon: 'exclamation-circle',
      title: 'No Physical Product Returns',
      content: 'All sales of NFC cards and physical accessories are FINAL. No returns or refunds.',
      color: 'from-red-500 to-pink-600'
    },
    {
      icon: 'mobile-alt',
      title: 'App Store Compliance',
      content: 'Subscriptions subject to Apple and Google store terms.',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      icon: 'globe',
      title: 'Worldwide Service',
      content: 'Available globally with region-specific terms where applicable.',
      color: 'from-green-500 to-emerald-600'
    }
  ];

  return (
    <>
      <section className="pt-20 md:pt-24 lg:pt-32 pb-20 bg-gradient-to-b from-white via-blue-50/30 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full filter blur-3xl" />
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="badge badge-blue inline-flex mb-6">
              <FontAwesomeIcon icon="file-contract" />
              <span>Legal</span>
            </div>
            
            <h1 className="text-h1 mb-6">
              Terms of <span className="text-gradient-blue-pink">Service</span>
            </h1>
            
            <p className="text-body-lg text-gray max-w-3xl mx-auto mb-8">
              Please read these terms carefully. By using AM CARDMATE, you agree to be bound by these terms.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-sm">
                <FontAwesomeIcon icon="calendar" className="text-blue-500" />
                <span className="text-body-sm"><strong>Last Updated:</strong> January 15, 2025</span>
              </div>
              <div className="flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-sm">
                <FontAwesomeIcon icon="globe" className="text-green-500" />
                <span className="text-body-sm"><strong>Jurisdiction:</strong> United Kingdom</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {importantNotes.map((note, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all h-full">
                    <div className={`w-14 h-14 bg-gradient-to-br ${note.color} rounded-xl flex items-center justify-center text-white mb-4`}>
                      <FontAwesomeIcon icon={note.icon} className="text-xl" />
                    </div>
                    <h3 className="text-h5 text-dark mb-2">{note.title}</h3>
                    <p className="text-body-sm text-gray">{note.content}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              <div className="lg:col-span-1">
                <div className="sticky top-24 bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 shadow-lg">
                  <h3 className="text-h5 text-dark mb-4">Quick Navigation</h3>
                  <nav className="space-y-2">
                    {sections.map((section, index) => (
                      <button
                        key={index}
                        onClick={() => setActiveSection(index)}
                        className={`w-full text-left px-4 py-2 rounded-lg transition-all ${
                          activeSection === index 
                            ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white' 
                            : 'hover:bg-gray-100 text-gray-600'
                        }`}
                      >
                        <FontAwesomeIcon icon={section.icon} className="mr-2 text-sm" />
                        <span className="text-body-sm">{section.title}</span>
                      </button>
                    ))}
                  </nav>
                </div>
              </div>

              <div className="lg:col-span-3 space-y-8">
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-3xl p-8">
                  <h2 className="text-h3 text-dark mb-4">Agreement Overview</h2>
                  <p className="text-body text-gray">
                    These Terms of Service ("Terms") constitute a legally binding agreement between you and AM CARDMATE Ltd. 
                    concerning your access to and use of the AM CARDMATE mobile application and related services. 
                    By using our service, you acknowledge that you have read, understood, and agree to be bound by these Terms.
                  </p>
                </div>

                <div className="bg-white rounded-3xl p-8 shadow-lg">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-purple-600 rounded-2xl flex items-center justify-center text-white">
                      <FontAwesomeIcon icon={sections[activeSection].icon} className="text-2xl" />
                    </div>
                    <h2 className="text-h3 text-dark">{sections[activeSection].title}</h2>
                  </div>
                  
                  {sections[activeSection].content && (
                    <p className="text-body text-gray">{sections[activeSection].content}</p>
                  )}
                  
                  {sections[activeSection].subsections && (
                    <div className="space-y-8 mt-8">
                      {sections[activeSection].subsections.map((subsection, subIndex) => (
                        <div key={subIndex}>
                          <h3 className="text-h4 text-dark mb-4">{subsection.subtitle}</h3>
                          <ul className="space-y-3">
                            {subsection.points.map((point, pointIndex) => (
                              <li key={pointIndex} className="flex items-start gap-3">
                                <FontAwesomeIcon icon="check-circle" className="text-green-500 mt-1 flex-shrink-0" />
                                <span className="text-body text-gray">{point}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-between items-center pt-8">
                  <button
                    onClick={() => setActiveSection(Math.max(0, activeSection - 1))}
                    className={`btn btn-outline ${activeSection === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={activeSection === 0}
                  >
                    <FontAwesomeIcon icon="arrow-left" />
                    <span>Previous</span>
                  </button>
                  
                  <div className="flex gap-2">
                    {sections.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setActiveSection(index)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          activeSection === index 
                            ? 'w-8 bg-gradient-to-r from-blue-500 to-purple-600' 
                            : 'bg-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  
                  <button
                    onClick={() => setActiveSection(Math.min(sections.length - 1, activeSection + 1))}
                    className={`btn btn-outline ${activeSection === sections.length - 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={activeSection === sections.length - 1}
                  >
                    <span>Next</span>
                    <FontAwesomeIcon icon="arrow-right" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-gradient-blue-pink text-white text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-dots-pattern opacity-15"></div>
        <div className="absolute top-20 left-10 w-24 h-24 rounded-full bg-white opacity-20"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 rounded-full bg-white opacity-10"></div>
        <div className="absolute top-1/3 right-1/4 w-16 h-16 rounded-full bg-white opacity-15"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-h2 mb-6">Questions About These Terms?</h2>
            <p className="text-body-lg text-white/90 mb-12">
              Our team is here to help clarify any questions about our Terms of Service.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
              <a 
                href="mailto:support@amcardmate.com"
                className="bg-white/20 backdrop-blur-sm rounded-xl p-6 hover:bg-white/30 transition-all flex items-center gap-4"
              >
                <FontAwesomeIcon icon="envelope" className="text-3xl" />
                <div className="text-left">
                  <p className="text-h5">Support</p>
                  <p className="text-body-sm text-white/80">support@amcardmate.com</p>
                </div>
              </a>
              
              <a 
                href="mailto:support@amcardmate.com"
                className="bg-white/20 backdrop-blur-sm rounded-xl p-6 hover:bg-white/30 transition-all flex items-center gap-4"
              >
                <FontAwesomeIcon icon="building" className="text-3xl" />
                <div className="text-left">
                  <p className="text-h5">Enterprise</p>
                  <p className="text-body-sm text-white/80">support@amcardmate.com</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default TermsOfService;